/** Chat API helpers — thin wrappers over the shared axios instance plus the
 *  chunked media upload (chat photos/videos ride the same resumable pattern
 *  as takeoff drawings) and a blob cache so thread media renders with auth. */
import api from "@/lib/api";

export const fetchPeople = () => api.get("/chat/people").then((r) => r.data);
export const fetchConversations = () => api.get("/chat/conversations").then((r) => r.data);
export const getConversation = (cid) => api.get(`/chat/conversations/${cid}`).then((r) => r.data);
export const createConversation = (memberIds, name, isGroup) =>
  api.post("/chat/conversations", { member_ids: memberIds, name: name || null, is_group: !!isGroup }).then((r) => r.data);
export const fetchMessages = (cid, before) =>
  api.get(`/chat/conversations/${cid}/messages`, { params: { before, limit: 50 } }).then((r) => r.data);
export const sendMessage = (cid, body, fileId, replyToId) =>
  api.post(`/chat/conversations/${cid}/messages`, { body: body || null, file_id: fileId || null, reply_to_id: replyToId || null }).then((r) => r.data);
export const markRead = (cid) => api.post(`/chat/conversations/${cid}/read`).then((r) => r.data);
export const sendTyping = (cid) => api.post(`/chat/conversations/${cid}/typing`).then((r) => r.data);
export const fetchUnread = () => api.get("/chat/unread").then((r) => r.data);
export const removeMember = (cid, userId) =>
  api.post(`/chat/conversations/${cid}/members/remove`, { user_id: userId }).then((r) => r.data);
export const renameConversation = (cid, name) =>
  api.post(`/chat/conversations/${cid}/rename`, { name }).then((r) => r.data);
export const deleteMessage = (cid, mid) =>
  api.delete(`/chat/conversations/${cid}/messages/${mid}`).then((r) => r.data);
export const deleteConversation = (cid) =>
  api.post(`/chat/conversations/${cid}/delete`).then((r) => r.data);

/** Superadmin only — read EliteBot's DMs as the bot (read-only). */
export const fetchBotConversations = () => api.get("/chat/bot/conversations").then((r) => r.data);
export const fetchBotMessages = (cid, before) =>
  api.get(`/chat/bot/conversations/${cid}/messages`, { params: { before, limit: 100 } }).then((r) => r.data);

/** Group chats are admin-created only. */
export const isChatAdmin = (role) => ["owner", "superadmin", "admin"].includes(role);

const CHUNK = 4 * 1024 * 1024;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/** Chunked, resumable-enough upload: each chunk retries on failure/stall. */
export async function uploadChatMedia(conversationId, file, onProgress) {
  const { data } = await api.post("/chat/media/start", {
    conversation_id: conversationId,
    name: file.name,
    mime: file.type,
    size: file.size,
  });
  const uploadId = data.upload_id;
  const chunks = Math.ceil(file.size / CHUNK);
  for (let i = 0; i < chunks; i += 1) {
    const offset = i * CHUNK;
    const blob = file.slice(offset, offset + CHUNK);
    let attempt = 0;
    for (;;) {
      const controller = new AbortController();
      let lastTick = Date.now();
      const watchdog = setInterval(() => {
        if (Date.now() - lastTick > 20000) controller.abort();
      }, 2000);
      try {
        await api.put(`/chat/media/${uploadId}/chunk?offset=${offset}`, blob, {
          headers: { "Content-Type": "application/octet-stream" },
          signal: controller.signal,
          onUploadProgress: () => { lastTick = Date.now(); },
        });
        clearInterval(watchdog);
        break;
      } catch (e) {
        clearInterval(watchdog);
        attempt += 1;
        if (attempt > 5) throw e;
        await sleep(500 * attempt);
      }
    }
    onProgress?.(Math.round(((i + 1) / chunks) * 100));
  }
  const done = await api.post(`/chat/media/${uploadId}/complete`);
  return done.data.file_id;
}

/* Blob cache: <img>/<video> can't send auth headers, so fetch once per file
 * and hand out object URLs. */
const mediaCache = new Map();
export async function mediaUrl(fileId) {
  if (mediaCache.has(fileId)) return mediaCache.get(fileId);
  const p = api
    .get(`/chat/media/${fileId}`, { responseType: "blob" })
    .then((r) => URL.createObjectURL(r.data))
    .catch(() => null);
  mediaCache.set(fileId, p);
  return p;
}
